import { Dispatch, useState } from 'react';
import { ChildIcon, UnisexIcon } from '@/components/Icons';
import { IUserDto, SelectChildDto } from '@/shared/interfaces';
import { R as ProfileResources } from '@/features/profile/resources/ProfileResources.resx';
import { useProfile } from '@/features/shared/lib/useProfile';
import { AddChildrenModal } from '@/features/shared/features/addOrEditChild/ui';
import { AddChildrenMode } from '@/features/shared/features/addOrEditChild/lib';
import { useUrlState } from '@/features/booking/shared/model/useUrlState';
import { format, R } from '@/features/booking/resources/Texts.resx';
import { HouseholdMemberType } from '@/shared/enums';
import { Card, NavigationItem, toast } from '@/components';
import { useAtomValue } from 'jotai';
import { adolescentMinimumAgeAtom } from '@/caregiverSpace/shared/model';
import { AdultHouseholdMemberWarningModal } from '@/features/booking/widgets/select-patient/ui/AdultHouseholdMemberWarningModal';

interface SelectPatientProps {
    onSelectPatient: (patientUid: string) => Promise<boolean>;
    showAddChildButton: boolean;
    showParent: boolean;
    children: SelectChildDto[];
    setChildren: Dispatch<React.SetStateAction<SelectChildDto[]>>;
    user: IUserDto;
}

export const SelectPatient = ({
    onSelectPatient,
    showAddChildButton,
    showParent,
    children,
    setChildren,
    user,
}: SelectPatientProps) => {
    const { urlState } = useUrlState();
    const { refetch } = useProfile();
    const [showAddChildModal, setShowAddChildModal] = useState(false);
    const [showHouseholdMemberModal, setShowHouseholdMemberModal] =
        useState(false);

    const adolescentMinimumAge = useAtomValue(adolescentMinimumAgeAtom);

    const isSelected = (uid: string) => {
        return uid === urlState.patientUid;
    };

    const handleRefetch = async () => {
        const updatedProfile = await refetch();

        if (updatedProfile?.error) {
            toast.addToast({
                type: 'error',
                message: ProfileResources.RefreshDataError,
                autoClose: true,
            });
            return;
        }

        const updatedChildren: SelectChildDto[] =
            updatedProfile?.data?.Kids.map((child) => ({
                ID: child.User.ID.toString(),
                Uid: child.User.UID,
                FullName: child.User.FullName,
            }));

        setChildren(updatedChildren);
    };

    return (
        <Card alwaysCard={false}>
            <div className="space-y-4">
                <h2 className="mb-6">{R.WhoNeedsHelp}</h2>

                {showParent && (
                    <NavigationItem
                        className={isSelected(user.UID) ? '' : ''}
                        onClick={() => onSelectPatient(user.UID)}
                        caption={format(R.Myself, user.FullName.split(' ')[0])}
                        icon={<UnisexIcon className="icon-box" />}
                    />
                )}

                {children?.map((child) => {
                    return (
                        <NavigationItem
                            key={child.Uid}
                            className={isSelected(child.Uid) ? '' : ''}
                            onClick={() => onSelectPatient(child.Uid)}
                            caption={child.FullName}
                            icon={<ChildIcon className="icon-box" />}
                        />
                    );
                })}

                {showAddChildButton && (
                    <NavigationItem
                        onClick={() => setShowAddChildModal(true)}
                        caption={
                            !children.length
                                ? format(
                                      R.AddFirstChild,
                                      adolescentMinimumAge.toString(),
                                  )
                                : format(
                                      R.AddChildUnderCertainAge,
                                      adolescentMinimumAge.toString(),
                                  )
                        }
                    />
                )}

                <NavigationItem
                    onClick={() => setShowHouseholdMemberModal(true)}
                    caption={format(
                        R.OtherHouseholdmembers,
                        adolescentMinimumAge.toString(),
                    )}
                />
            </div>

            {showAddChildModal && (
                <AddChildrenModal
                    title={ProfileResources.ChooseChildren}
                    allowedTypes={[HouseholdMemberType.Child]}
                    mode={AddChildrenMode.Booking}
                    onClose={() => setShowAddChildModal(false)}
                    onChildAdded={(uuid) => {
                        setShowAddChildModal(false);
                        if (uuid) {
                            onSelectPatient(uuid);
                        }
                        handleRefetch();
                    }}
                />
            )}

            {showHouseholdMemberModal && (
                <AdultHouseholdMemberWarningModal
                    onClose={() => setShowHouseholdMemberModal(false)}
                    adolescentMinimumAge={adolescentMinimumAge}
                />
            )}
        </Card>
    );
};
